<script setup lang="ts">
import { InfoTip, localizedPath } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type EfficiencyTier, TIER_NAMES } from '../model/efficiency'
import type { EnergyEstimate, TierCost } from '../model/energy'
import { REFERENCE_PATH } from '../model/paths'

defineProps<{
  energy: EnergyEstimate | null
  /** Every tier for the same build and usage, for the comparison table. */
  tiers: readonly TierCost[]
  tier: EfficiencyTier
}>()

const { t, n, locale } = useI18n()

const watts = (value: number): string =>
  t('form.watts', { value: n(value, { maximumFractionDigits: 0 }) })
const kwh = (value: number, digits = 0): string =>
  t('energy.kwh', { value: n(value, { maximumFractionDigits: digits }) })
const percent = (value: number): string => n(value, { style: 'percent', maximumFractionDigits: 0 })
// No currency symbol: the price was typed in the visitor's own currency, which
// the tool never needs to know.
const money = (value: number): string =>
  n(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const referenceHref = computed(() => localizedPath(REFERENCE_PATH, locale.value))
</script>

<template>
  <section class="panel" aria-labelledby="energy-heading">
    <h2 id="energy-heading" class="panel__title">{{ t('energy.heading') }}</h2>

    <template v-if="energy">
      <p class="panel__headline" data-testid="energy">
        <span class="panel__value mono">{{
          n(energy.kwhPerYear, { maximumFractionDigits: 0 })
        }}</span>
        <!-- A leading space, as in the PSU headline: "735 kWh a year". -->
        <span class="panel__value-label">{{ ` ${t('energy.perYear')}` }}</span>
      </p>
      <p v-if="energy.costPerYear !== null" class="energy__cost">
        {{ t('energy.cost', { value: money(energy.costPerYear) }) }}
        <InfoTip :label="t('energy.costLabel')" :text="t('energy.costHelp')" />
      </p>
      <p v-else class="energy__cost energy__cost--missing">{{ t('energy.noPrice') }}</p>

      <dl class="energy__list">
        <div class="energy__row">
          <dt>{{ t('energy.perDay') }}</dt>
          <dd class="mono">{{ kwh(energy.kwhPerDay, 2) }}</dd>
        </div>
        <div class="energy__row">
          <dt>
            {{ t('energy.wall') }}
            <InfoTip :label="t('energy.wall')" :text="t('energy.wallHelp')" />
          </dt>
          <dd class="mono">
            {{
              t('energy.split', {
                heavy: watts(energy.heavyWallWatts),
                light: watts(energy.lightWallWatts),
              })
            }}
          </dd>
        </div>
        <div class="energy__row">
          <dt>{{ t('energy.efficiency') }}</dt>
          <dd class="mono">
            {{
              t('energy.split', {
                heavy: percent(energy.heavyEfficiency),
                light: percent(energy.lightEfficiency),
              })
            }}
          </dd>
        </div>
        <div v-if="energy.monitorWatts > 0" class="energy__row">
          <dt>{{ t('energy.monitors') }}</dt>
          <dd class="mono">{{ watts(energy.monitorWatts) }}</dd>
        </div>
      </dl>

      <details class="energy__compare">
        <summary>{{ t('energy.compare') }}</summary>
        <div class="energy__table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">{{ t('energy.colTier') }}</th>
                <th scope="col">{{ t('energy.colKwh') }}</th>
                <th v-if="energy.costPerYear !== null" scope="col">{{ t('energy.colCost') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in tiers"
                :key="row.tier"
                :class="{ energy__selected: row.tier === tier }"
              >
                <th scope="row">
                  {{ TIER_NAMES[row.tier] }}
                  <span v-if="row.tier === tier" class="visually-hidden">
                    {{ t('energy.selected') }}
                  </span>
                </th>
                <td class="mono">{{ n(row.kwhPerYear, { maximumFractionDigits: 0 }) }}</td>
                <td v-if="row.costPerYear !== null" class="mono">{{ money(row.costPerYear) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </template>

    <p v-else class="panel__empty">{{ t('results.empty') }}</p>

    <a class="energy__reference" :href="referenceHref">{{ t('energy.reference') }}</a>
  </section>
</template>

<style scoped>
.energy__cost {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin: calc(-1 * var(--space-2)) 0 var(--space-4);
  font-size: var(--text-lg);
}

.energy__cost--missing {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.energy__list {
  margin: 0;
}

.energy__row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--space-1) var(--space-3);
  padding: var(--space-2) 0;
  border-top: 1px solid var(--color-border);
  font-size: var(--text-sm);
}

dt {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-muted);
}

dd {
  margin: 0;
}

.energy__compare {
  margin-top: var(--space-4);
  font-size: var(--text-sm);
}

.energy__compare summary {
  cursor: pointer;
  color: var(--color-accent);
}

.energy__table-wrap {
  overflow-x: auto;
  margin-top: var(--space-2);
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: var(--space-1) var(--space-2);
  border-bottom: 1px solid var(--color-border);
  text-align: right;
  white-space: nowrap;
}

th[scope='row'],
th:first-child {
  text-align: left;
  font-weight: 400;
}

thead th {
  color: var(--color-text-muted);
  font-weight: 500;
}

.energy__selected {
  background: var(--color-accent-subtle);
}

.energy__selected th {
  font-weight: 600;
}

.energy__reference {
  display: inline-block;
  margin-top: var(--space-4);
  font-size: var(--text-sm);
}
</style>
